import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, styled, TextField, Typography } from '@material-ui/core';

import CustomMap from '../../../../components/Map';
import { PointsType } from '../../../../interfaces/points';

const InputWrapper = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  '& .MuiInputLabel-root': {
    color: '#979797 !important',
    backgroundColor: '#fff',
    paddingRight: '5px',
  },
  '& .MuiOutlinedInput-root': {
    paddingRight: 0,
  },
}));

const WrapperBox = styled('div')({
  position: 'relative',
  width: '100%',
});

const DropdownWrapper = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  maxHeight: 300,
  overflowY: 'auto',
  zIndex: 1000,
  boxShadow: theme.shadows[3],
  width: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
}));

const OptionItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

interface PickupLocationSelectProps {
  value: PointsType;
  onChange: (store: any) => void;
  stores: any[];
  error?: boolean;
  helperText?: string;
  intl: any;
  isLoading: boolean;
  disabled?: boolean;
}

const PickupLocationSelect: React.FC<PickupLocationSelectProps> = ({
  value,
  onChange,
  stores,
  error,
  helperText,
  intl,
  isLoading,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [points, setPoints] = useState(stores);

  useEffect(() => {
    if (stores) {
      setPoints(stores);
    }
  }, [stores]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <WrapperBox ref={wrapperRef}>
      {openMap && !isLoading ? (
        <CustomMap
          loading={isLoading}
          open={!!openMap}
          selectedPoint={value}
          pickupPoints={points}
          handleSelectPickup={onChange}
          handleClose={() => setOpenMap(false)}
        />
      ) : null}
      <InputWrapper
        fullWidth
        variant='outlined'
        label={intl.formatMessage({ id: 'STORE.INPUT.LOCATION' })}
        placeholder={intl.formatMessage({ id: 'STORE.SELECT_PICKUP_POINT' })}
        value={
          value.city || value.city_name
            ? `${value?.city || value?.city_name} ${value?.address}`
            : ''
        }
        onClick={() => setOpenMap(true)}
        error={error}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        InputProps={{
          readOnly: true,
        }}
      />

      {isOpen && (
        <DropdownWrapper>
          {stores &&
            stores?.map(store => (
              <OptionItem
                key={store?.uuid}
                onClick={() => {
                  onChange(store);
                  setIsOpen(false);
                }}
              >
                <Typography>
                  {store?.name || intl.formatMessage({ id: 'STORE.NO_ADDRESS' })}
                </Typography>
              </OptionItem>
            ))}
        </DropdownWrapper>
      )}
    </WrapperBox>
  );
};

export default PickupLocationSelect;
